import { t } from "i18next";
import elementService from "../../../js/common/ElementService";
import pageMeasureService from "../measure/PageMeasureService";
import ajaxClient from "../../../js/common/AjaxClient";
import { AddProductToBasketResult } from "../basket/basketHooks";

interface MultipleProductsToBasketResponse {
    basketBox: string;
    dataLayer: Record<string, unknown>[];
    results: {
        productId: number;
        status: AddProductToBasketResult | "error";
        maximalQuantity?: number;
        message: string;
    }[];
}

const addMultipleProductsToBasket = async (productIds: number[]): Promise<MultipleProductsToBasketResponse> => {
    const parameters = new URLSearchParams();

    for (const id of productIds) {
        parameters.append("productIds[]", id.toString());
    }

    return ajaxClient.post<MultipleProductsToBasketResponse>(
        "/Basket/jsAddMultipleProductsToBasket",
        parameters,
    );
};

export default class SharedBasketPageService {
    public init(): void {
        this.setEventListeners();
        this.decorateAddAllButton();
    }

    public setEventListeners(): void {
        const addToBasketButtons = document.querySelectorAll<HTMLButtonElement>("button.sb-btn-add[data-product-id]");

        for (const button of addToBasketButtons) {
            button.addEventListener("click", (event) => {
                event.preventDefault();
                const productId = elementService.elementData(button, "productId");
                this.handleAddToBasket([Number(productId)], false).catch((error: unknown) => {
                    throw new Error(`Error adding product to basket: ${error instanceof Error ? error.message : String(error)}`);
                });
            });
        }

        const addAllToBasketButton = elementService.querySelector<HTMLAnchorElement>("#sb-btn-add-all");

        if (addAllToBasketButton instanceof HTMLButtonElement) {
            addAllToBasketButton.addEventListener("click", (event) => {
                event.preventDefault();

                const productIds = [...document.querySelectorAll<HTMLButtonElement>("button.sb-btn-add[data-product-id]")]
                    .map((button) => Number(elementService.elementData(button, "productId")))
                    .filter((id) => !Number.isNaN(id));
                this.handleAddToBasket(productIds, true).catch((error: unknown) => {
                    throw new Error(`Error adding all products to basket: ${error instanceof Error ? error.message : String(error)}`);
                });
            });
        }
    }

    private decorateAddAllButton(): void {
        const allProducts = document.querySelectorAll<HTMLDivElement>("div.sb-item[data-product-id]");
        const allProductsCount = allProducts.length;
        const productsAddable = document.querySelectorAll<HTMLButtonElement>("button.sb-btn-add[data-product-id]");
        const productsAddableCount = productsAddable.length;
        const productsAdded = document.querySelectorAll<HTMLSpanElement>("span.sb-result-success[data-product-id]");
        const productsAddedCount = productsAdded.length;
        const productsFailed = document.querySelectorAll<HTMLSpanElement>("span.sb-result-error[data-product-id]");
        const productsFailedCount = productsFailed.length;

        const addAllButton = elementService.querySelector<HTMLButtonElement>("button#sb-btn-add-all");
        addAllButton.style.setProperty("--total", String(allProductsCount - productsFailedCount));
        addAllButton.style.setProperty("--filled", String(productsAddedCount));

        if (productsAddableCount === 0) {
            addAllButton.style.display = "none";
            const resultTag = elementService.querySelector<HTMLSpanElement>("span#sb-result-all");
            resultTag.classList.remove("sb-result-loading");
            const basketLink = elementService.querySelector<HTMLAnchorElement>("a#sb-basket-link");
            basketLink.style.display = "block";
            resultTag.style.display = "block";

            if (productsAddedCount > 0) {
                resultTag.classList.add("sb-result-success");
                resultTag.innerHTML = "<i class='fa-solid fa-check'></i> přidáno";
            } else {
                resultTag.style.display = "none";
            }
        }
    }

    private async handleAddToBasket(productIds: number[], addAll: boolean): Promise<void> {
        const addAllButton = elementService.querySelector<HTMLButtonElement>("button#sb-btn-add-all");

        if (addAll) {
            const resultTag = elementService.querySelector<HTMLSpanElement>("span#sb-result-all");
            addAllButton.style.display = "none";
            resultTag.style.display = "block";
        }

        for (const productId of productIds) {
            this.removeAddToBasketButtom(productId);
        }

        const response = await addMultipleProductsToBasket(productIds);

        for (const result of response.results) {
            const resultTag = elementService.querySelector<HTMLSpanElement>(
                `span.sb-result[data-product-id="${result.productId}"]`,
            );
            resultTag.classList.remove("sb-result-loading");

            switch (result.status) {
                case "success":
                    resultTag.classList.add("sb-result-success");
                    resultTag.innerHTML = `<i class='fa-solid fa-check-circle fa-lg'></i> ${t("basket:added")}`;
                    break;
                case "already_in_basket":
                    resultTag.classList.add("sb-result-success");
                    resultTag.innerHTML = `<i class='fa-solid fa-check-circle fa-lg'></i> ${t("basket:sharedBasketAlreadyInBasket")}`;
                    break;
                case "sold_out":
                    resultTag.classList.add("sb-result-error");
                    resultTag.innerHTML = `<i class='fa-solid fa-times-circle'></i> ${t("basket:sharedBasketSoldout")}`;
                    break;
                case "too_many_package_of_week":
                    resultTag.classList.add("sb-result-error");
                    resultTag.innerHTML = `<i class='fa-solid fa-times-circle'></i> ${t("basket:sharedBasketTooManyPackageOfWeek", { maximalQuantity: result.maximalQuantity })}`;
                    break;
                case "error":
                    resultTag.classList.add("sb-result-error");
                    resultTag.innerHTML = `<i class='fa-solid fa-times-circle'></i> ${t("basket:sharedBasketFailedToAdd")}`;
                    break;
            }
        }

        const basketBoxElement = elementService.querySelector<HTMLDivElement>("div#js-basket-header-container");
        basketBoxElement.innerHTML = response.basketBox;

        pageMeasureService.sendDataLayer(response.dataLayer);

        if (addAll) {
            const resultTag = elementService.querySelector<HTMLSpanElement>("span#sb-result-all");
            const basketLink = elementService.querySelector<HTMLAnchorElement>("a#sb-basket-link");

            resultTag.classList.remove("sb-result-loading");
            resultTag.classList.add("sb-result-success");
            resultTag.innerHTML = `<i class='fa-solid fa-check-circle'></i> ${t("basket:added")}`;
            basketLink.style.display = "block";
        }

        this.decorateAddAllButton();
    }

    private removeAddToBasketButtom(productId: number): void {
        const button = elementService.querySelector<HTMLButtonElement>(`button.sb-btn-add[data-product-id="${productId}"]`);
        const resultTag = elementService.querySelector<HTMLSpanElement>(`span.sb-result[data-product-id="${productId}"]`);

        button.remove();
        resultTag.style.display = "block";
    }
}
